/**
 * Phase 7 — D56: Acuity-pattern deep links.
 *
 * Parses query string params at widget mount so ad landing pages can
 * pre-select a service / stylist / contact info. Stable IDs only — no
 * slugs — so catalog renames don't break ad URLs.
 *
 * Supported params (single-service flow today; multi-service + category +
 * datetime are deferred to a follow-up):
 *   ?service=svc_id           preselect one service
 *   ?staff=stf_id             preselect stylist (skip Staff step)
 *   ?firstName=Jane           prefill Contact step
 *   ?lastName=Doe
 *   ?email=jane@example.com
 *   ?phone=+15551234567
 *
 * UTM passthrough (utm_source, utm_medium, utm_campaign, utm_term,
 * utm_content) is handled by captureAttribution() in firetrack.ts —
 * preserved automatically through any fallback path.
 *
 * Hardening:
 * - Each value is capped at 256 chars so a malicious URL can't stuff
 *   the page with megabytes of input
 * - IDs aren't trusted client-side beyond presence — bootstrap will
 *   reject unknown service/staff IDs and the App state machine drops
 *   the preselect with a banner notice
 */

const MAX_VALUE_LEN = 256;

function pick(params: URLSearchParams, key: string): string | null {
  const v = params.get(key);
  if (!v) return null;
  const trimmed = v.trim();
  if (!trimmed) return null;
  return trimmed.length > MAX_VALUE_LEN ? trimmed.slice(0, MAX_VALUE_LEN) : trimmed;
}

export interface DeepLinkParams {
  preselectedServiceId: string | null;
  preselectedStaffId: string | null;
  prefilledContact: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
  };
  // Phase 7 — CRO message-match. The campaign creative says "$75
  // Balayage" → the landing page says "$75 Balayage" → the widget hero
  // says "$75 Balayage". `?headline=` is explicit; `utm_content` is the
  // automatic fallback so existing tracked campaigns get message-match
  // for free without needing the operator to add another param.
  headline: string | null;
  // Phase 7 — D57b. Per-link theme override slug. Widget passes this to
  // the bootstrap call as `?theme=`; the server resolves it against
  // WidgetTheme rows. Unknown slugs silently fall back to base branding.
  themeSlug: string | null;
  // Phase 7+. Category deep link. Accepts a custom WidgetServiceGroup ID
  // (UUID) OR a Square category ID — the widget tries the custom group
  // first, then falls back to the Square category. Unknown ID surfaces
  // the same "menu updated" banner as a stale service preselect.
  categoryId: string | null;
  // Phase 7+. Search-bar pre-fill. CMS pages link to specific services
  // by name (?q=chemical+peel); the widget pre-populates the catalog
  // search input and auto-expands the row when exactly one design
  // service matches. Different from ?service=<id> which skips the
  // Service step entirely. BeYou-variant only currently — other
  // tenants' Service step ignores it.
  searchQuery: string | null;
  // Phase 6 — open a Special Service's purchase directly (?product=<id>):
  // an ad or a page about one prescription lands on its checkout. Also how
  // a hidden test product is reached on the live widget.
  productId?: string | null;
}

export function parseDeepLinkParams(href?: string): DeepLinkParams {
  if (typeof window === 'undefined' && !href) {
    return emptyParams();
  }
  const url = new URL(href ?? window.location.href);
  const sp = url.searchParams;

  return {
    preselectedServiceId: pick(sp, 'service'),
    preselectedStaffId: pick(sp, 'staff'),
    prefilledContact: {
      firstName: pick(sp, 'firstName'),
      lastName: pick(sp, 'lastName'),
      email: pick(sp, 'email'),
      phone: pick(sp, 'phone'),
    },
    headline: pick(sp, 'headline') ?? pick(sp, 'utm_content'),
    themeSlug: pick(sp, 'theme'),
    categoryId: pick(sp, 'category'),
    searchQuery: pick(sp, 'q'),
    productId: pick(sp, 'product'),
  };
}

export function hasAnyDeepLinkParams(p: DeepLinkParams): boolean {
  return !!(
    p.preselectedServiceId ||
    p.preselectedStaffId ||
    p.prefilledContact.firstName ||
    p.prefilledContact.lastName ||
    p.prefilledContact.email ||
    p.prefilledContact.phone ||
    p.headline ||
    p.themeSlug ||
    p.categoryId ||
    p.searchQuery ||
    p.productId
  );
}

function emptyParams(): DeepLinkParams {
  return {
    preselectedServiceId: null,
    preselectedStaffId: null,
    prefilledContact: {
      firstName: null,
      lastName: null,
      email: null,
      phone: null,
    },
    headline: null,
    themeSlug: null,
    categoryId: null,
    searchQuery: null,
  };
}
